// ======================================================================
// File: src/modules/ppe/pages/procurement/purchaseInvoice/list/PurchaseInvoiceListPage.jsx
// Module: PPE → Procurement → Purchase Invoice
//
// Purpose:
// - Purchase Invoice List Page
// - KPI Cards
// - Filters
// - Invoice Listing
// - Create Purchase Invoice
// - Open Purchase Invoice Drawer
// - Navigate To Invoice Detail
// ======================================================================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./PurchaseInvoiceListPage.css";

import purchaseInvoiceService from "../services/purchaseInvoiceService";
import PurchaseInvoiceFormDrawer from "../form/PurchaseInvoiceFormDrawer";

export default function PurchaseInvoiceListPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [rows, setRows] = useState([]);

  const [filters, setFilters] = useState({
    pi_no: "",
    supplier_invoice_no: "",
    status: "",
  });
  const [currentPage, setCurrentPage] = useState(1);

const pageSize = 10;

  const formatDate = value => {
  if (!value) return "";

  return new Date(value).toLocaleDateString(
    "en-GB",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  );
};

const formatAmount = value =>
  Number(value || 0).toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

  // ----------------------------------------------------------
  // LOAD DATA
  // ----------------------------------------------------------

  const loadData = async () => {
    try {
      setLoading(true);

      const response =
        await purchaseInvoiceService.getPurchaseInvoiceList();

      setRows(response?.data || []);
    } catch (error) {
      console.error(error);
      alert("Failed to load Purchase Invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
  setCurrentPage(1);
}, [filters]);

  // ----------------------------------------------------------
  // FILTER CHANGE
  // ----------------------------------------------------------

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ----------------------------------------------------------
  // FILTERED DATA
  // ----------------------------------------------------------

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const piMatch =
        !filters.pi_no ||
        row.pi_no
          ?.toLowerCase()
          .includes(filters.pi_no.toLowerCase());

      const supplierMatch =
        !filters.supplier_invoice_no ||
        row.supplier_invoice_no
          ?.toLowerCase()
          .includes(
            filters.supplier_invoice_no.toLowerCase()
          );

      const statusMatch =
        !filters.status ||
        row.status === filters.status;

      return piMatch && supplierMatch && statusMatch;
    });
  }, [rows, filters]);
  const totalPages = Math.ceil(
      filteredRows.length / pageSize
    );

    const paginatedRows = filteredRows.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );
  // ----------------------------------------------------------
  // KPI DATA
  // ----------------------------------------------------------

  const kpis = useMemo(() => {
    return {
      total: rows.length,

      draft: rows.filter(
        (x) => x.status === "DRAFT"
      ).length,

      submitted: rows.filter(
        (x) => x.status === "SUBMITTED"
      ).length,

      approved: rows.filter(
        (x) => x.status === "APPROVED"
      ).length,

      posted: rows.filter(
        (x) => x.status === "POSTED"
      ).length,
    };
  }, [rows]);

  // ----------------------------------------------------------
  // OPEN DETAIL
  // ----------------------------------------------------------

  const openDetail = (id) => {
    navigate(
      `/ppe/procurement/purchase-invoice/${id}`
    );
  };

  return (
    <div className="container-fluid">

      {/* ===================================================== */}
      {/* HEADER */}
      {/* ===================================================== */}

      <div className="grn-header mb-4">

        <div>
          <h2 className="grn-title">
            Purchase Invoice Management
          </h2>

          <div className="grn-subtitle">
            Manage supplier invoices and accounting integration
          </div>
        </div>

        <button
          className="btn btn-light"
          onClick={() => setDrawerOpen(true)}
        >
          + Create Purchase Invoice
        </button>

      </div>

      {/* ===================================================== */}
      {/* KPI CARDS */}
      {/* ===================================================== */}

      <div className="row g-3 mb-3">

        <div className="col-md">
          <div className="stat-card stat-total">
            <div className="stat-label">
              Total Invoices
            </div>

            <div className="stat-value">
              {kpis.total}
            </div>
          </div>
        </div>

        <div className="col-md">
          <div className="stat-card stat-draft">
            <div className="stat-label">
              Draft
            </div>

            <div className="stat-value">
              {kpis.draft}
            </div>
          </div>
        </div>

        <div className="col-md">
          <div className="stat-card stat-submitted">
            <div className="stat-label">
              Submitted
            </div>

            <div className="stat-value">
              {kpis.submitted}
            </div>
          </div>
        </div>

      <div className="col-md">
        <div className="stat-card stat-approved">
          <div className="stat-label">
            Approved
          </div>

          <div className="stat-value">
            {kpis.approved}
          </div>
        </div>
      </div>

       <div className="col-md">
        <div className="stat-card stat-posted">
          <div className="stat-label">
            Posted
          </div>

          <div className="stat-value">
            {kpis.posted}
          </div>
        </div>
      </div>

      </div>

      {/* ===================================================== */}
      {/* FILTERS */}
      {/* ===================================================== */}

      <div className="card filter-panel mb-3">

        <div className="card-header">
          Search & Filters
        </div>

        <div className="card-body">

          <div className="row g-4">

            <div className="col-md-4">
              <label className="form-label">
                PI No
              </label>

              <input
                type="text"
                className="form-control"
                name="pi_no"
                value={filters.pi_no}
                onChange={handleFilterChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">
                Supplier Invoice No
              </label>

              <input
                type="text"
                className="form-control"
                name="supplier_invoice_no"
                value={filters.supplier_invoice_no}
                onChange={handleFilterChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">
                Status
              </label>

              <select
                className="form-select"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">
                  All
                </option>

                <option value="DRAFT">
                  DRAFT
                </option>

                <option value="SUBMITTED">
                  SUBMITTED
                </option>

                <option value="APPROVED">
                  APPROVED
                </option>

                <option value="POSTED">
                  POSTED
                </option>

                <option value="CANCELLED">
                  CANCELLED
                </option>
              </select>
            </div>

          </div>

        </div>
      </div>

      {/* ===================================================== */}
      {/* TABLE */}
      {/* ===================================================== */}

      <div className="card table-card">

        <div className="card-header">
          <span>Purchase Invoice List</span>

          <span className="record-count">
            {filteredRows.length} Record(s)
          </span>
        </div>

        <div className="card-body">

          <div className="table-responsive table-scroll">

            <table className="table table-bordered table-hover">

              <thead>
                <tr>
                  <th>PI No</th>
                  <th>PI Date</th>
                  <th>Vendor</th>
                  <th>PO No</th>
                  <th>PO Date</th>
                  <th>Supplier Invoice No</th>
                  <th>Invoice Type</th>
                  <th>Net Value</th>
                  <th>Tax Value</th>
                  <th>Total Value</th>
                  <th>Status</th>
                  <th width="120">Actions</th>
                </tr>
              </thead>

              <tbody>

                {loading && (
                  <tr>
                    <td
                      colSpan="12"
                      className="text-center"
                    >
                      Loading...
                    </td>
                  </tr>
                )}

                {!loading &&
                  filteredRows.length === 0 && (
                    <tr>
                      <td
                        colSpan="12"
                        className="text-center"
                      >
                        No Records Found
                      </td>
                    </tr>
                  )}

                {!loading &&
                  paginatedRows.map((row) => (
                    <tr key={row.id}>

                      <td>
                        <button
                          className="btn btn-link p-0"
                          onClick={() =>
                            openDetail(row.id)
                          }
                        >
                          {row.pi_no}
                        </button>
                      </td>

                      <td>{formatDate(row.pi_date)}</td>
                      <td>{row.vendor_name}</td>
                      <td>{row.po_no}</td>
                      <td>{formatDate(row.po_date)}</td>

                      <td>
                        {row.supplier_invoice_no}
                      </td>

                      <td>{row.invoice_type}</td>

                      <td>{formatAmount(row.net_value)}</td>

                      <td>{formatAmount(row.tax_value)}</td>

                      <td>{formatAmount(row.total_value)}</td>

                      <td>
                        <span
                          className={`status-badge status-${row.status?.toLowerCase()}`}
                        >
                          {row.status}
                        </span>
                      </td>

                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() =>
                            openDetail(row.id)
                          }
                        >
                          View
                        </button>
                      </td>

                    </tr>
                  ))}

              </tbody>

            </table>

          </div>
          {/* PAGINATION START */}

            <div className="d-flex justify-content-between align-items-center mt-3">

              <div className="record-count">
                Showing {(currentPage - 1) * pageSize + 1}
                {" - "}
                {Math.min(
                  currentPage * pageSize,
                  filteredRows.length
                )}
                {" of "}
                {filteredRows.length}
              </div>

              <div className="d-flex gap-2">

                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage(currentPage - 1)
                  }
                >
                  Previous
                </button>

                <span className="align-self-center">
                  Page {currentPage} of {totalPages || 1}
                </span>

                <button
                  className="btn btn-sm btnoutline-secondary"
                  disabled={currentPage >= totalPages}
                  onClick={() =>
                    setCurrentPage(currentPage + 1)
                  }
                >
                  Next
                </button>

              </div>

            </div>

            {/* PAGINATION END */}

        </div>
      </div>

      {/* ===================================================== */}
      {/* CREATE DRAWER */}
      {/* ===================================================== */}

      <PurchaseInvoiceFormDrawer
        open={drawerOpen}
        mode="create"
        onClose={() => setDrawerOpen(false)}
        onSaved={(invoice) => {

          loadData();

          setDrawerOpen(false);

          if (invoice?.id) {
            navigate(
              `/ppe/procurement/purchase-invoice/${invoice.id}`
            );
          }
        }}
      />

    </div>
  );
}